import styled from 'styled-components';
import { cloneDeep } from 'lodash';
import {
  AccountClient,
  apiResolver,
  GET_CLIENTS,
  TicketClient,
  GET_TECHNICIANS_BY_CRITERIA
} from '@nile/graphql';
import { COMMON_CONSTANTS } from '@nile/components';

const getAccessor = (col) => {
  switch (col.name) {
    case 'worklog.worklogDate':
      return 'worklog.worklogDate';
    case 'worklog.client':
      return 'worklog.client.name';
    case 'billDate':
      return 'billDate';
    case 'displayId':
      return 'workItem.displayId';

    case 'worklog.notes':
      return 'worklog.notes';
    case 'status':
      return 'worklog.ticket.status';
    case 'client':
      return 'client.name';
    case 'technician.name':
      return 'technician.name';
    case 'serviceItem':
    case 'serviceItem.name':
      return 'serviceItem.name';
    case 'serviceCatalogItem':
    case 'serviceCatalogItem.name':
      return 'serviceCatalogItem.name';
    case 'unitsOrHours':
    case 'qty':
      return 'qty';
    case 'quantity':
      return 'qty';
    case 'unitPrice':
      return 'unitPrice';
    case 'amount':
    case 'revisedUnitPrice':
    case 'revisedQuantity':
    case 'revisedAmount':
      return col.name;
    case 'contract':
      return 'contract.contract.name';
    default:
      return col.name;
  }
};

const getAttributeName = (column) => {
  if (column?.name) {
    switch (column.name) {
      case 'prioritizedId':
        return 'displayId';
      case 'displayId':
        return 'workItem.displayId';
      case 'client':
        return 'client.name';
      case 'site':
        return 'site.name';
      case 'requester':
        return 'requester.name';
      case 'technician':
        return 'technician.name';
      case 'techGroup':
        return 'techGroup.name';
      case 'firstResponseTimer':
        return 'firstResponseDueTime';
      case 'resolutionTimer':
        return 'resolutionDueTime';
      case 'sla':
        return 'sla.name';
      case 'contract':
        return 'contract.contract.name';
      default:
        return column.name;
    }
  }
  return '';
};

const getType = (col) => {
  switch (col.name) {
    case 'revisedQuantity':
    case 'revisedUnitPrice':
    case 'revisedDiscountRate':
      return 'Edit';
    case 'displayId':
      return 'TicketProjectId';
    case 'unitPrice':
    case 'amount':
      return 'Currency';
    case 'revisedAmount':
      return 'Total';
    case 'billDate':
    case 'worklog.worklogDate':
      return 'DateTime';
    case 'serviceItem':
    case 'serviceItem.name':
    case 'serviceCatalogItem':
    case 'serviceCatalogItem.name':
      return 'ServiceItem';
    case 'notes':
      return 'Notes';
    case 'contract':
      return 'Contract';
    default:
      return 'Text';
  }
};

const getCellType = (col) => {
  switch (col.name) {
    case 'displayId':
      return 'TicketProjectId';
    default:
      return 'Text';
  }
};

const isEditable = (col) => {
  switch (col.name) {
    default:
      return false;
  }
};

const isSortable = (col) => {
  switch (col.name) {
    case 'billDate':
    case 'client':
    case 'notes':
    case 'worklog.notes':
    case 'serviceItem.name':
    case 'serviceCatalogItem.name':
    case 'qty':
    case 'unitPrice':
    case 'discountRate':
    // case 'amount':
    case 'revisedQuantity':
    case 'revisedUnitPrice':
    case 'revisedDiscountRate':
    case 'revisedAmount':
    case 'site.name':
    case 'displayId':
    case 'contract':
      return true;
    default:
      return false;
  }
};

const isFilterable = (col) => {
  switch (col?.name) {
    case 'worklog.client':
    case 'worklog.worklogDate':
    case 'client':
    case 'billDate':
    case 'technician.name':
    case 'serviceItem.name':
    case 'serviceCatalogItem.name':
    case 'site.name':
    case 'displayId':
    case 'unitPrice':
    case 'revisedAmount':
    case 'contract':
      return true;
    default:
      return false;
  }
};

const getRuleAttribute = (f) => {
  switch (f.name) {
    case 'prioritizedId':
      return 'displayId';
    case 'displayId':
      return 'workItem.displayId';
    case 'client':
      return 'client.name';
    case 'contract':
      return 'contract.contract.name';
    default:
      return f.name;
  }
};

const getWidth = (col) => {
  switch (col.name) {
    case 'notes':
    case 'worklog.notes':
      return 400;
    case 'serviceItem':
    case 'serviceItem.name':
    case 'serviceCatalogItem':
    case 'serviceCatalogItem.name':
      return '250';
    case 'contract':
      return '340';
    default:
      return 150;
  }
};

const getVariables = (property) => {
  switch (property.name) {
    case 'worklog.client':
    case 'client':
      return { listInfo: {} };
    default:
      return null;
  }
};

const getDefaultValue = (f, condition) => {
  const operands = cloneDeep(condition.operands);
  const ruleIdx = operands.findIndex((el) => {
    if (f.dataType === 'entity') {
      return el.attribute === `${f.name}.name`;
    }
    return el.attribute === getRuleAttribute(f);
  });
  if (ruleIdx > -1) {
    const rule = operands[ruleIdx];
    switch (f.dataType) {
      case 'date':
      case 'datetime':
      case 'daterange':
        return rule.value;
      default:
        break;
    }
    switch (f.filterType) {
      case 'text':
      case 'numeric':
      case 'decimal':
      case 'paragraph':
      case 'read_only':
        return rule.value;
      default:
        return rule.value.map((el) => {
          return {
            name: el,
            value: el,
            label: el
          };
        });
    }
  }
  return '';
};
const getMethodName = (field) => {
  switch (field) {
    default:
      return 'name';
  }
};

const getKeyName = (field) => {
  switch (field) {
    default:
      return 'name';
  }
};

const checkDynamicQuery = (f) => {
  switch (f.name) {
    case 'worklog.client':
    case 'client':
    case 'technician.name':
      return true;
    default:
      return false;
  }
};

const getDynamicQuery = (f) => {
  const resolverModule = 'Ticket';
  let client = TicketClient;
  let query = apiResolver[resolverModule][f.name]?.api;
  const variables = apiResolver?.[resolverModule]?.[f?.name]?.variables || {};
  switch (f.name) {
    case 'worklog.client':
    case 'client':
      client = AccountClient;
      query = GET_CLIENTS;
      return client.query({
        query: query,
        variables: {
          listInfo: {
            sort: {
              attribute: 'name',
              order: 'ASC'
            }
          }
        }
      });
    default:
      query = apiResolver[resolverModule][f.name]?.api;
      client = apiResolver[resolverModule][f.name]?.client;
      return client.query({
        query: query,
        variables: variables
      });
  }
};

const generateOptions = (f, data) => {
  const options = [];
  let allValues = [];
  if (f.isCustom) {
    const fldOptions =
      data?.getFields?.options || data?.getFields?.fieldOptions;
    allValues = fldOptions?.map((el) => {
      return {
        name: el?.value,
        value: el?.value,
        label: el?.value
      };
    });
    return allValues;
  }
  const methodName = getMethodName(f.name);
  const key = getKeyName(f.name);
  switch (f.name) {
    case 'client':
    case 'worklog.client':
      allValues = data.getClientList.clients.map((el) => {
        return {
          ...el,
          name: el.accountId,
          value: el.name,
          label: el.name
        };
      });
      break;
    default:
      allValues = [];
  }
  return allValues;
};

const updateConditions = (params) => {
  const { condition, selectedField, viewConditions, setConditions } = params;
  if (condition?.operands?.length) {
    const currentConditions = cloneDeep(viewConditions) || {
      joinOperator: 'AND',
      operands: []
    };
    let updatedCondition = {};
    const ruleIdx = currentConditions.operands.findIndex((el) =>
      selectedField.dataType === 'entity'
        ? el.attribute === `${selectedField.name}.name`
        : el.attribute === getRuleAttribute(selectedField)
    );

    if (ruleIdx > -1) {
      currentConditions.operands[ruleIdx] = condition.operands[0];
      updatedCondition = currentConditions;
    } else {
      updatedCondition = {
        ...currentConditions,
        operands: currentConditions.operands.concat(condition.operands)
      };
    }
    setConditions(updatedCondition);
  } else {
    const currentConditions = cloneDeep(viewConditions);
    if (currentConditions) {
      const ruleIdx = currentConditions.operands.findIndex((el) =>
        selectedField.dataType === 'entity'
          ? el.attribute === `${selectedField.name}.name`
          : el.attribute === getRuleAttribute(selectedField)
      );
      if (ruleIdx > -1) {
        currentConditions.operands.splice(ruleIdx, 1);
        setConditions(
          currentConditions?.operands?.length
            ? {
                ...currentConditions,
                operands: currentConditions.operands
              }
            : null
        );
      }
    }
  }
};

const getQuery = (f) => {
  switch (f.name) {
    case 'technician.name':
      return GET_TECHNICIANS_BY_CRITERIA;
    default:
      return GET_TECHNICIANS_BY_CRITERIA;
  }
};

const getAsyncOptions = (property) => {
  return { client: AccountClient, query: getQuery(property) };
};

function getNameKey(property) {
  switch (property.name) {
    case 'technician.name':
      return 'getTechnicianList';
    default:
      return 'getTechnicianList';
  }
}

const getRuleAtrribute = (property) => {
  const attribute =
    property?.name?.split('.')?.length > 1
      ? property?.name.split('.')[1]
      : property?.name || 'name';

  switch (attribute) {
    default:
      return attribute;
  }
};

const fetchValues = async (client, query, params) => {
  const attribute = getRuleAtrribute(params.property);

  const filterOperands = [
    {
      attribute: attribute || 'name',
      operator: 'startsWith',
      value: params.inputValue
    }
  ];

  const nameKey = getNameKey(params.property);
  const variables = {
    listInfo: {
      condition: {
        joinOperator: 'AND',
        operands: filterOperands
      }
    }
  };
  const res = await client.query({
    query: query,
    variables: COMMON_CONSTANTS.IS_INSIDE_INPUT_VARIABLES[nameKey]
      ? { input: variables }
      : variables
  });
  const allOptions = res?.data?.[nameKey]
    ? res.data[nameKey].userList.map((el) => {
        return {
          name: `${el[attribute]}`,
          value: `${el[attribute]}`,
          label: `${el[attribute]}`
        };
      })
    : [];

  return allOptions;
};

const isAutoComplete = (col) => {
  switch (col.name) {
    case 'technician.name':
      return true;
    default:
      return false;
  }
};

const checkCustomAttribute = (attr, field) => {
  return attr === getRuleAttribute(field);
};
export {
  getAccessor,
  getType,
  getCellType,
  getWidth,
  isSortable,
  isEditable,
  isFilterable,
  getRuleAttribute,
  getVariables,
  checkDynamicQuery,
  getDynamicQuery,
  updateConditions,
  getDefaultValue,
  generateOptions,
  getMethodName,
  getKeyName,
  isAutoComplete,
  fetchValues,
  getAsyncOptions,
  getAttributeName,
  checkCustomAttribute
};
